import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Calendar, Loader2, Printer } from 'lucide-react';
import { shiftApi } from '@/services/api';
import type { POSShift, ShiftCloseReport } from '@/types';
import { browserPrintShiftReceipt } from '@/utils/shiftReceiptPrint';
import { formatMoney } from '@/utils/currency';

export default function ZReportSection() {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selected, setSelected] = useState<string>('');
  const [detailType, setDetailType] = useState<'invoice' | 'item'>('invoice');

  const { data: shifts = [], isLoading: loadingList } = useQuery({
    queryKey: ['closedShiftsForDate', date],
    queryFn: async () => {
      const res = await shiftApi.listClosedShiftsForDate(date);
      return res.data.message as POSShift[];
    },
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['zReport', selected, detailType],
    queryFn: async () => {
      const res = await shiftApi.getZReport(selected, detailType);
      return res.data.message as ShiftCloseReport;
    },
    enabled: !!selected,
    retry: false,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="w-5 h-5 text-gray-400" />
          <input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setSelected('');
            }}
            className="border-gray-200 rounded-lg text-sm"
          />
          <select
            className="border border-gray-200 rounded-lg text-sm px-2 py-1.5 min-w-[200px]"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Select closed shift…</option>
            {shifts.map((s) => (
              <option key={s.name} value={s.name}>
                {s.z_number != null ? `Z#${s.z_number} · ` : ''}
                {s.cashier_name || s.cashier} · {s.pocket_name} · {s.name}
              </option>
            ))}
          </select>
          <select
            className="border border-gray-200 rounded-lg text-sm px-2 py-1.5"
            value={detailType}
            onChange={(e) => setDetailType(e.target.value as 'invoice' | 'item')}
          >
            <option value="invoice">Invoice detail</option>
            <option value="item">Item detail</option>
          </select>
        </div>
        <button
          type="button"
          disabled={!data}
          onClick={() => data && browserPrintShiftReceipt({ ...data, report_type: 'Z' })}
          className="text-sm px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <Printer className="w-4 h-4" /> Print Z
        </button>
      </div>

      {loadingList && (
        <p className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading shifts…
        </p>
      )}

      {!loadingList && shifts.length === 0 && (
        <p className="text-sm text-gray-500">No closed shifts for this date.</p>
      )}

      {isLoading && selected && (
        <p className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Z report…
        </p>
      )}

      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {(error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to load Z report'}
        </div>
      )}

      {data && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-gray-900">
              Z Report{data.z_number != null ? ` #${data.z_number}` : ''} — {data.name}
            </h3>
            <button type="button" className="text-xs text-gray-500 underline" onClick={() => void refetch()}>
              Refresh
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Cashier</p>
              <p className="font-medium">{data.cashier_name || data.cashier}</p>
            </div>
            <div>
              <p className="text-gray-500">Total sales</p>
              <p className="font-medium">{formatMoney(data.total_sales || 0)}</p>
            </div>
            <div>
              <p className="text-gray-500">Counted / Expected</p>
              <p className="font-medium">
                {formatMoney(data.closing_amount || 0)} / {formatMoney(data.expected_cash || 0)}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Variance</p>
              <p
                className={`font-medium ${
                  (data.difference || 0) < 0 ? 'text-red-600' : 'text-emerald-600'
                }`}
              >
                {formatMoney(data.difference || 0)}
              </p>
            </div>
            {data.handover_mode === 'Bank' && data.handover_bank && (
              <div>
                <p className="text-gray-500">Handover To (Bank)</p>
                <p className="font-medium">{data.handover_bank}</p>
              </div>
            )}
            {data.handover_mode === 'Cash' && data.handover_to_name && (
              <div>
                <p className="text-gray-500">Handover To (Manager)</p>
                <p className="font-medium">{data.handover_to_name}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
